import * as React from 'react';
import {
  getGetSiteInductionChecklistQueryKey, getGetVisitorInductionQueryKey, getListMySiteInductionsQueryKey,
  listMySiteInductions, useGetSiteInductionChecklist, useGetVisitorInduction,
  useCompleteMySiteInduction, useCompleteVisitorInduction
} from '@cdf/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import { PolicyAcceptanceSheet } from '@/components/booking/policy-acceptance-sheet';
import { usePolicyAdministrationContext, uncertainPolicyWrite } from '@/hooks/use-policy-administration-context';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { getErrorMessage } from '@/lib/utils';

type Props = {
  open: boolean; siteId: string | null; siteName?: string | null; spaceId?: string | null; approvalId?: string;
  onOpenChange: (open: boolean) => void; onCompleted?: () => void;
};
export function SiteInductionSheet(props: Props) {
  const context = usePolicyAdministrationContext();
  return <ScopedInduction key={context.key + ':' + props.spaceId + ':' + props.open} {...props} request={context.request} />;
}
function ScopedInduction(props: Props & {request: ReturnType<typeof usePolicyAdministrationContext>['request']}) {
  const [legacy, setLegacy] = React.useState(false);
  const continuing = React.useRef(false);
  if (!props.spaceId || legacy) return <LegacySiteInductionSheet {...props} />;
  return <PolicyAcceptanceSheet open={props.open} spaceId={props.spaceId} locationName={props.siteName}
    onCompleted={async () => {
      const statuses = await listMySiteInductions(undefined, props.request);
      if (props.siteId && statuses.items.some(status => status.siteId === props.siteId && !status.isComplete)) {
        continuing.current = true; setLegacy(true);
      } else props.onCompleted?.();
    }}
    onOpenChange={open => { if (!open && continuing.current) { continuing.current = false; return; } props.onOpenChange(open); }} />;
}
/** Compatibility only for a requirement whose legacy mapping cannot be proved. */
export function LegacySiteInductionSheet(props: Props) {
  const context = usePolicyAdministrationContext();
  return <LegacyReview key={context.key + ':' + props.siteId + ':' + props.approvalId + ':' + props.open} {...props} context={context} />;
}
function LegacyReview({open,siteId,siteName,approvalId,onOpenChange,onCompleted,context}:
  Props & {context:ReturnType<typeof usePolicyAdministrationContext>}) {
  const client=useQueryClient();
  const member=useGetSiteInductionChecklist(siteId??'',{query:{
    queryKey:[...getGetSiteInductionChecklistQueryKey(siteId??''),context.key],
    enabled:open && !!siteId && !approvalId && context.ready,retry:false},request:context.request});
  const visitor=useGetVisitorInduction(approvalId??'',{query:{
    queryKey:[...getGetVisitorInductionQueryKey(approvalId??''),context.key],
    enabled:open && !!siteId && !!approvalId,retry:false},request:context.request});
  const query=approvalId?visitor:member;
  const checklist=approvalId?visitor.data?.checklist:member.data;
  const complete=useCompleteMySiteInduction({mutation:{retry:false},request:context.request});
  const completeGuest=useCompleteVisitorInduction({mutation:{retry:false},request:context.request});
  const [checked,setChecked]=React.useState<Set<string>>(()=>new Set());
  const [version,setVersion]=React.useState<number|null>(null);
  const [error,setError]=React.useState<string|null>(null);
  const [locked,setLocked]=React.useState(false);
  const [saved,setSaved]=React.useState(false);
  const [busy,setBusy]=React.useState(false);
  const pending=React.useRef(false),mounted=React.useRef(true);
  React.useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  React.useEffect(()=>{if(version===null && checklist)setVersion(checklist.version);},[checklist,version]);
  const stale=version!==null && checklist?.version!==version;
  async function refresh() {
    if(pending.current)return;pending.current=true;setBusy(true);
    try {
      const result=await query.refetch();
      if(result.isError)throw result.error;
      if(mounted.current){setVersion(null);setChecked(new Set());setLocked(false);setError(null);}
    }catch(err){if(mounted.current)setError(getErrorMessage(err));}
    finally{pending.current=false;if(mounted.current)setBusy(false);}
  }
  async function confirm() {
    if(pending.current || !siteId || !checklist || stale || locked || query.isError || query.isFetching ||
      checklist.items.some(item=>!checked.has(item.id)))return;
    pending.current=true;setBusy(true);setError(null);
    try {
      const data={siteId,version:checklist.version,itemIds:checklist.items.map(item=>item.id)};
      if(approvalId)await completeGuest.mutateAsync({id:approvalId,data});else await complete.mutateAsync({data});
      if(!mounted.current)return;setSaved(true);
      await client.invalidateQueries({queryKey:getListMySiteInductionsQueryKey()});
      if(approvalId)await client.invalidateQueries({queryKey:getGetVisitorInductionQueryKey(approvalId)});
    }catch(err){if(mounted.current){setLocked(true);setError(uncertainPolicyWrite(err)?
      'The outcome could not be confirmed. Refresh before trying again.':getErrorMessage(err));}}
    finally{pending.current=false;if(mounted.current)setBusy(false);}
  }
  return <Sheet open={open} onOpenChange={value=>{if(!pending.current)onOpenChange(value);}}>
    <SheetContent side="bottom" className="mx-auto flex max-h-[92dvh] w-full max-w-3xl flex-col gap-0 rounded-t-2xl p-0">
      <SheetHeader className="border-b p-5 pr-12"><SheetTitle>Site requirements</SheetTitle>
        <SheetDescription>{checklist?.siteName || siteName || 'Selected site'} · existing requirements retained for this site.</SheetDescription></SheetHeader>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
        {query.isPending?<p>Loading requirements…</p>:query.isError?<p role="alert">{getErrorMessage(query.error)}</p>:null}
        {stale && <p role="alert">Requirements changed. Refresh and review them.</p>}
        {checklist?.items.map(item=><label key={item.id} className="flex items-start gap-3 rounded-lg border p-3">
          {!saved && <input type="checkbox" checked={checked.has(item.id)} disabled={busy || locked || stale}
            onChange={event=>{const on=event.target.checked;setChecked(previous=>{const next=new Set(previous);if(on)next.add(item.id);else next.delete(item.id);return next;});}}/>}
          <span><strong>{item.label}</strong>{item.detail && <span className="block whitespace-pre-wrap text-sm">{item.detail}</span>}</span>
        </label>)}
        {saved && <p role="status">Site requirements accepted. Review and confirm your booking or check-in again.</p>}
        {error && <p role="alert" className="text-destructive">{error}</p>}
      </div>
      <div className="flex justify-end gap-2 border-t p-5">
        <Button variant="outline" disabled={busy} onClick={()=>void refresh()}>Refresh requirements</Button>
        {saved?<Button disabled={busy || stale} onClick={()=>{onCompleted?.();onOpenChange(false);}}>Return to booking or check-in</Button>:
          <Button disabled={!checklist || busy || locked || stale || query.isError || query.isFetching ||
            checklist.items.some(item=>!checked.has(item.id))} onClick={()=>void confirm()}>Accept site requirements</Button>}
      </div>
    </SheetContent>
  </Sheet>;
}
